import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { useGetOne, useRecordContext } from 'ra-core';
import type { CustomerInvoice, GoodsReceipt, PurchaseOrder, VendorBill } from '../../types';

export const ThreeWayMatchBadge = ({ resource }: { resource: 'customer_invoices' | 'vendor_bills' }) => {
    const record = useRecordContext<CustomerInvoice | VendorBill>();
    const poId = record?.po_id;
    const grnId = record?.grn_id;

    const { data: po, isPending: isLoadingPO } = useGetOne<PurchaseOrder>(
        'purchase_orders',
        { id: poId! },
        { enabled: !!poId }
    );

    const { data: grn, isPending: isLoadingGRN } = useGetOne<GoodsReceipt>(
        'goods_receipts',
        { id: grnId! },
        { enabled: !!grnId }
    );

    if (!record || !poId || !grnId) return null;
    if (isLoadingPO || isLoadingGRN || !po || !grn) return null;

    const isSales = resource === 'customer_invoices';
    
    // Matching checks
    const poMatched = !!po;
    const grnMatched = grn.status !== 'draft' && grn.status !== 'qa_failed';
    const amountMatched = Math.abs((record.total_amount || 0) - (po.total_amount || 0)) < 1; 

    return (
        <div className="flex flex-col gap-2 mt-2">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{isSales ? 'Sales' : 'Procurement'} 3-Way Match</span>
            <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className={`gap-1 ${poMatched ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500'}`}>
                    {poMatched ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                    PO Matched
                </Badge>
                <Badge variant="outline" className={`gap-1 ${grnMatched ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                    {grnMatched ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                    GRN Matched
                </Badge>
                <Badge variant="outline" className={`gap-1 ${amountMatched ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                    {amountMatched ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                    Value Matched
                </Badge>
            </div>
        </div>
    );
};
